import React, { useMemo, useState } from "react";
import { employees, rosters, shifts } from "../data/sampleData";
import { detectBackToBack, getWorkedShifts } from "../../utils/calculateFatigue";
import "./SiteRosterMatrix.css";
import SiteRosterDashboar from "./SiteRosterDashboard";

/* ===========================
   DATE UTILS
=========================== */
function parseRosterMonth(monthStr) {
  const [, mm, yyyy] = monthStr.split("-");
  return new Date(Number(yyyy), Number(mm) - 1, 1);
}

function getMonthDays(year, month) {
  const days = [];
  const d = new Date(year, month, 1);
  while (d.getMonth() === month) {
    days.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

/* ===========================
   AI ENGINE
=========================== */
function useRecommendations(siteEmployees, days, getCellModel) {
  return useMemo(() => {
    const issues = [];

    siteEmployees.forEach(emp => {
      days.forEach(date => {
        const cell = getCellModel(emp.id, date);

        cell.deviations.forEach(d =>
          issues.push({
            employee: emp.name,
            date,
            type: d.type,
            shift: d.shift
          })
        );

        if (cell.isFatigue)
          issues.push({ employee: emp.name, date, type: "FATIGUE" });

        if (cell.isAbsent)
          issues.push({ employee: emp.name, date, type: "ABSENT" });
      });
    });

    const recommendations = [];
    if (issues.some(i => i.type === "MISSED"))
      recommendations.push("Reassign missed shifts or add backup staff.");
    if (issues.some(i => i.type === "UNPLANNED"))
      recommendations.push("Update roster to reflect actual work.");
    if (issues.some(i => i.type === "FATIGUE"))
      recommendations.push("Rotate staff to reduce consecutive shifts.");
    if (issues.some(i => i.type === "ABSENT"))
      recommendations.push("Add buffer manpower for absentee-prone days.");

    return { issues, recommendations };
  }, [siteEmployees, days, getCellModel]);
}

/* ===========================
   AI PANEL
=========================== */
function RecommendationPanel({ open, onClose, data }) {
  if (!open) return null;

  /* ===========================
     GROUP ISSUES BY EMPLOYEE
  ========================== */

  const issueCounts = data.issues.reduce((acc, i) => {
  acc[i.type] = (acc[i.type] || 0) + 1;
  return acc;
}, {});

  const issuesByEmployee = data.issues.reduce((acc, issue) => {
    if (!acc[issue.employee]) acc[issue.employee] = [];
    acc[issue.employee].push(issue);
    return acc;
  }, {});

  /* ===========================
     UNIQUE RECOMMENDATIONS
  ========================== */
  const uniqueRecommendations = [...new Set(data.recommendations)];

  return (
    <div className="ai-panel">
      <div className="ai-panel-header" style={{fontSize: "25px"}}>
       <div className="ai-header-summary">
        🤖 AI Insights
        <div className="ai-issue-counts">
          {Object.entries(issueCounts).map(([type, count]) => (
            <span key={type} className={`issue-pill ${type}`}>
              {type}: {count}
            </span>
          ))}
        </div>
      </div>

        <button onClick={onClose}>✕</button>
      </div>

      <div className="ai-panel-body">

        {/* ===== AI RECOMMENDATIONS (TOP) ===== */}
        <div className="ai-recommendations">
          <h4>Recommended Actions</h4>
          <ul>
            {uniqueRecommendations.map((r, i) => (
              <li key={i} className="ai-reco">
                💡 {r}
              </li>
            ))}
          </ul>
        </div>

        {/* ===== EMPLOYEE ISSUE GROUPS ===== */}
        <h4 style={{ marginTop: 20 }}>Detected Issues by Employee</h4>

       <div className="ai-employee-list">
        {Object.entries(issuesByEmployee).map(
          ([employee, issues]) => {

            // 🔹 aggregate counts per issue type
            const counts = issues.reduce((acc, i) => {
              acc[i.type] = (acc[i.type] || 0) + 1;
              return acc;
            }, {});

            return (
              <div key={employee} className="ai-employee-card">
                <div className="ai-employee-name">
                  👤 {employee}
                </div>

                <div className="ai-employee-issue-summary">
                  {Object.entries(counts).map(([type, count]) => (
                    <div
                      key={type}
                      className={`issue-summary-pill ${type}`}
                    >
                      <span className="issue-type">{type}</span>
                      <span className="issue-count">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
        )}
      </div>


      </div>
    </div>
  );
}


/* ===========================
   MAIN COMPONENT
=========================== */
export default function SiteRosterMatrix({ site }) {
  const today = new Date();
  const [cursor] = useState({
    year: today.getFullYear(),
    month: today.getMonth()
  });

  const [activeFilter, setActiveFilter] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const [viewMode, setViewMode] = useState("MATRIX");

  const days = useMemo(
    () => getMonthDays(cursor.year, cursor.month),
    [cursor]
  );

  /* ===========================
     ROSTERS
  ========================== */
  const siteRosters = useMemo(() => {
    return rosters.filter(r => {
      if (r.siteId !== site.siteId) return false;
      const m = parseRosterMonth(r.month);
      return (
        m.getFullYear() === cursor.year && m.getMonth() === cursor.month
      );
    });
  }, [site, cursor]);

  const rosterMap = useMemo(() => {
    const map = {};
    days.forEach(d => (map[d] = {}));

    siteRosters.forEach(r =>
      r.employees.forEach(e => {
        days.forEach(d => {
          map[d][e.employeeId] = map[d][e.employeeId] || [];
          map[d][e.employeeId].push({
            shift: e.shift,
            siteId: r.siteId
          });
        });
      })
    );

    return map;
  }, [siteRosters, days]);

  const rosterEmployeeIds = useMemo(() => {
    const s = new Set();
    siteRosters.forEach(r =>
      r.employees.forEach(e => s.add(e.employeeId))
    );
    return s;
  }, [siteRosters]);

  const siteEmployees = useMemo(
    () => employees.filter(e => rosterEmployeeIds.has(e.id)),
    [rosterEmployeeIds]
  );

  /* ===========================
     WORKED SHIFTS (ONE TIME)
  ========================== */
  const workedShiftMap = useMemo(() => {
    const map = {};
    days.forEach(date => {
      map[date] = {};
      siteEmployees.forEach(emp => {
        map[date][emp.id] = getWorkedShifts(emp.id, date) || [];
      });
    });
    return map;
  }, [days, siteEmployees]);


  /* ===========================
     CELL MODEL
  ========================== */
  function getCellModel(empId, date) {
    const planned =
      rosterMap[date]?.[empId]
        ?.filter(s => s.siteId === site.siteId)
        .map(s => s.shift) || [];

    const worked = workedShiftMap[date]?.[empId] || [];

    const completed = [];
    const unplanned = [];
    const otherUnit = [];

    worked.forEach(w => {
      if (planned.includes(w.shift) && w.siteId === site.siteId)
        completed.push(w.shift);
      else if (!planned.includes(w.shift) && w.siteId === site.siteId)
        unplanned.push(w.shift);
      else if (w.siteId !== site.siteId)
        otherUnit.push({shift:w.shift, otherunitId: w.siteId});
    });

    const missed = planned.filter(s => !completed.includes(s));

    const deviations = [
      ...missed.map(s => ({ type: "MISSED", shift: s })),
      ...unplanned.map(s => ({ type: "UNPLANNED", shift: s }))
    ];

    return {
      planned,
      completed,
      unplanned,
      otherUnit,
      deviations,
      isAbsent: planned.length > 0 && completed.length === 0,
      isFatigue: detectBackToBack(empId, date)
    };
  }

  /* ===========================
     FILTER
  ========================== */
  function employeeVisible(emp) {
    if (!activeFilter) return true;

    if (["ABSENT", "FATIGUE"].includes(activeFilter)) {
      return days.some(d => {
        const c = getCellModel(emp.id, d);
        return activeFilter === "ABSENT"
          ? c.isAbsent
          : c.isFatigue;
      });
    }

    return days.some(d => {
      const c = getCellModel(emp.id, d);
      return (
        c.planned.includes(activeFilter) ||
        c.completed.includes(activeFilter) ||
        c.unplanned.includes(activeFilter)
      );
    });
  }

  const aiData = useRecommendations(
    siteEmployees,
    days,
    getCellModel
  );

  /* ===========================
     RENDER
  ========================== */
  return (
    <div className="roster-container">
<div className="view-toggle-slider">
  <input
    type="radio"
    name="viewMode"
    id="matrixView"
    checked={viewMode === "MATRIX"}
    onChange={() => setViewMode("MATRIX")}
  />
  <input
    type="radio"
    name="viewMode"
    id="dashboardView"
    checked={viewMode === "DASHBOARD"}
    onChange={() => setViewMode("DASHBOARD")}
  />

  <label htmlFor="matrixView" className="toggle-label">
    <span role="img" aria-label="Matrix">🧩</span>
  </label>
  <label htmlFor="dashboardView" className="toggle-label">
    <span role="img" aria-label="Dashboard">📊</span>
  </label>

  <div className="toggle-slider"></div>
</div>


      {/* ===== MONTHLY CAPACITY SUMMARY ===== */}
    <div style={{ marginBottom: "20px", padding: "15px",
        background: "#b1add8",
        borderRadius: "20px",
        maxWidth: "50%"
      }}
    >
  <h4 style={{ marginBottom: "10px" }}>
    Monthly Staffing Summary
  </h4>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr",
      border: "2px solid #b1add8",
      borderRadius: "6px",
      overflow: "hidden",
      background: "#ffffff"
    }}
  >
    {/* ===== HEADER ===== */}
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
        padding: "10px 12px",
        fontWeight: "700",
        background: "#f5f3ff",
        borderBottom: "1px solid #d1d5db"
      }}
    >
      <div>Shift</div>
      <div>Required</div>
      <div>Worked</div>
      <div>Deviation</div>
    </div>

    {/* ===== ROWS ===== */}
    {Object.entries(site.minRequiredPerShift).map(
      ([shift, dailyRequired]) => {
        const monthlyRequired =
          dailyRequired * days.length;

        let workedCount = 0;
        days.forEach(d =>
          siteEmployees.forEach(emp => {
            if (
              workedShiftMap[d][emp.id].some(
                w => w.shift === shift
              )
            ) {
              workedCount++;
            }
          })
        );

        const deviation =
          workedCount - monthlyRequired;

        const bgColor =
          deviation < 0
            ? "#fee2e2"
            : deviation > 0
            ? "#dcfce7"
            : "#ecfeff";

        return (
          <div
            key={shift}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              padding: "10px 12px",
              borderBottom: "1px solid #e5e7eb",
              background: bgColor,
              alignItems: "center",
              fontSize: "14px"
            }}
          >
            <div style={{ fontWeight: "600" }}>
              {shift}
            </div>

            <div>{monthlyRequired}</div>

            <div>{workedCount}</div>

            <div style={{ fontWeight: "700" }}>
              {deviation > 0 && "+"}
              {deviation}
            </div>
          </div>
        );
      }
    )}
  </div>
</div>

      
      {/* ===== MATRIX ===== */}
     {viewMode === "MATRIX" && (
        
      <div className="matrix-page">

        <div className="view-modes">
          <button className="active">
            Planned + Deviations
          </button>
          <button onClick={() => window.print()}>
            🖨 Print
          </button>
          <button
            className="ai-btn"
            onClick={() => setShowAI(true)}
          >
            🤖 AI
          </button>
        </div>


      {/* LEGEND */}
      <div className="legend">
        {Object.entries(shifts).map(([k, s]) => (
          <span key={k} className="legend-item">
            <span className="dot" style={{ background: s.color }} /> {k}
          </span>
        ))}
        <span className="legend-item"><span className="dot absent" /> Absent</span>
        <span className="legend-item"><span className="dot fatigue" /> Fatigue</span>
        <span className="legend-item"><span className="dot" style={{ background: "#ef4444" }} /> Missed</span>
        <span className="legend-item"><span className="dot" style={{ background: "#3b82f6" }} /> Unplanned</span>
        <span className="legend-item"><span className="dot" style={{ background: "#9ca3af" }} /> Other Unit</span>
      </div>

      {/* FILTER CARDS */}
      <div className="summary-cards">
        {["ABSENT", "FATIGUE"].map(k => (
          <div
            key={k}
            className={`card ${activeFilter === k ? "active" : ""}`}
            onClick={() => setActiveFilter(p => (p === k ? null : k))}
          >
            {k}
           
          </div>
        ))}
      </div>

      <div className="summary-cards shifts">
        {Object.entries(shifts).map(([k, cfg]) => (
          <div
            key={k}
            className={`card shift ${activeFilter === k ? "active" : ""}`}
            style={{ borderColor: cfg.color }}
            onClick={() => setActiveFilter(p => (p === k ? null : k))}
          >
            {k}
          
          </div>
        ))}
      </div>

        <div className="matrix-wrapper">
          <table className="roster-matrix">
            <thead>
              <tr>
                <th className="sticky-left sticky-top">
                  Employee
                </th>
                <th className="sticky-left sticky-top">
                  Working Days
                </th>
                {days.map(d => (
                  <th key={d} className="sticky-top">
                    {d.slice(8, 10)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {siteEmployees
                .filter(employeeVisible)
                .map(emp => {
                  const totalWorkedDays = days.filter(
                    d =>
                      workedShiftMap[d][emp.id].length >
                      0
                  ).length;

                  return (
                    <tr key={emp.id}>
                      <td className="emp-cell sticky-left">
                        {emp.name} <br/> {emp.employeeId}
                      </td>
                      <td className="sticky-left" style={{padding: '15px'}}>                                                
                          {totalWorkedDays}                         
                      </td>

                         {days.map(date => {
                  const cell = getCellModel(emp.id, date);
                  return (
                    <td key={date} className={cell.isAbsent ? "absent" : ""}>
                    <div className="cell-wrapper">

                        {/* PLANNED SHIFTS (completed OR missed) */}
                        {cell.planned.map(s => {
                          const isCompleted = cell.completed.includes(s);

                          return (
                            <span
                              key={`p-${s}`}
                              className={`shift-pill ${isCompleted ? "completed" : "missed"}`}
                              style={{ background: isCompleted ? "#22c55e" : "#ef4444" }}
                              title={isCompleted ? "Planned & Completed" : "Planned but Missed"}
                            >
                              {s}
                            </span>
                          );
                        })}

                        {/* UNPLANNED – SAME UNIT */}
                        {cell.unplanned.map(s => (
                          <span
                            key={`u-${s}`}
                            className="shift-pill unplanned"
                            style={{ background: "#3b82f6" }}
                            title="Worked without plan (this unit)"
                          >
                            {s}
                          </span>
                        ))}

                        {/* WORKED IN OTHER UNIT */}
                        {cell.otherUnit.map(s => (
                          <span
                            key={`o-${s.shift}`}
                            className="shift-pill other-unit"
                            style={{ background: "#9ca3af" }}
                            title="Worked in another unit"
                          >
                            {s.shift}
                          </span>
                        ))}

                        {/* TOOLTIP */}
                        <div className="cell-tooltip">
                          <strong>{emp.name}</strong>
                          {/*<div>{date}</div>*/}
                          <div>Planned: {cell.planned.join(", ") || "-"}</div>
                           {cell.completed.length > 0 && (
                            <div>Completed: {cell.completed.join(", ") || "-"}</div>
                           )}
                          {cell.unplanned.length > 0 && (
                            <div>Unplanned (this unit): {cell.unplanned.join(", ")}</div>
                          )}
                          {cell.otherUnit.length > 0 && (
                            <div>
                              Other unit: {cell.otherUnit.map(o => `${o.shift} @ ${o.otherunitId}`).join(", ")}
                            </div>
                          )}
                          {cell.isFatigue && <div className="warn">Fatigue Risk</div>}
                        </div>

                      </div>

                    </td>
                  );
                })}
             
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        <RecommendationPanel
          open={showAI}
          onClose={() => setShowAI(false)}
          data={aiData}
        />
      </div>      
      )}

    {viewMode === "DASHBOARD" && (
      <SiteRosterDashboar
        days={days}
        siteEmployees={siteEmployees}
        rosterMap={rosterMap}
        site={site}
        getWorkedShifts={getWorkedShifts}
        detectBackToBack={detectBackToBack}
        onFilter={(val) => {
          setActiveFilter(val); // SAME summary cards logic
          setViewMode("MATRIX");
        }}
      />
    )}
    </div>
  );
}
